import React from "react";
import { AnimatePresence } from "framer-motion";
import { Button, ButtonGroup } from "./Button/index";
import { ModalBackdrop, ModalContent } from "./Motion";
import { Trash2 } from "lucide-react";
/**
 * ConfirmationDialog - Reusable confirmation dialog component
 * 
 * A generic confirmation dialog with customizable message, confirm/cancel actions,
 * and backdrop overlay. Styled to match DeletePersonaModal for consistency.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls dialog visibility
 * @param {string} props.title - Dialog title (default: "Confirm Action")
 * @param {string} props.message - Main message to display
 * @param {string} props.highlightText - Text to highlight in red (optional)
 * @param {string} props.confirmText - Text for confirm button (default: "Confirm")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {Function} props.onConfirm - Callback when confirm is clicked
 * @param {Function} props.onCancel - Callback when cancel is clicked
 * @param {boolean} props.showIcon - Whether to show trash icon (default: true)
 * @param {string} props.subtitle - Additional subtitle text (optional)
 */
const ConfirmationDialog = ({
    isOpen,
    title = "Confirm Action",
    message,
    highlightText,
    confirmText = "Confirm",
    cancelText = "Cancel",
    onConfirm,
    onCancel,
    showIcon = true,
    subtitle,
}) => {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop with blur */}
                    <ModalBackdrop onClick={onCancel} />

                    {/* Modal - Centered */}
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
                        <ModalContent className="w-full max-w-md pointer-events-auto">
                            <div className="bg-white rounded-2xl shadow-2xl p-6">
                                {/* Icon */}
                                {showIcon && (
                                    <div className="flex justify-center mb-4">
                                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                                            <Trash2 className="w-6 h-6 text-red-500" />
                                        </div>
                                    </div>
                                )}

                                {/* Title */}
                                <h3 className="text-xl font-bold text-center text-gray-900 mb-2">
                                    {title}
                                </h3>

                                {/* Message */}
                                <p className="text-center text-gray-600 mb-6">
                                    {message}{" "}
                                    {highlightText && (
                                        <span className="font-semibold text-red-500">"{highlightText}"</span>
                                    )}
                                    ?
                                    {subtitle && (
                                        <>
                                            <br />
                                            {subtitle}
                                        </>
                                    )}
                                </p>

                                {/* Buttons */}
                                <ButtonGroup spacing="lg" className="w-full">
                                    <Button
                                        variant="secondary"
                                        rounded="full"
                                        size="md"
                                        onClick={onCancel}
                                        fullWidth
                                    >
                                        {cancelText}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        rounded="full"
                                        size="md"

                                        onClick={onConfirm}
                                        fullWidth
                                    >
                                        {confirmText}
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </ModalContent>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ConfirmationDialog;
